/**
 * @file     dealFinder.ts
 * @readme   ../../CLAUDE.md
 * @purpose  English strings for the deal finder / lead flow feature: SEO, page copy, step labels, options, and results.
 */
export const dealFinder = {
  seo: {
    title: 'Start here | Buy, sell, or find tenants in Vancouver',
    description:
      'Pick where to start in Vancouver: buying, selling or finding tenants. Then answer a few short questions in a guided flow.',
    ogTitle: 'Start here | Buy, sell or rent out',
    ogDescription: 'A simple place to start if you want to buy, sell or rent out.'
  },
  page: {
    title: 'Start here',
    description:
      'A few questions, then your dashboard, with a direct line to a realtor.'
  },
  intent: {
    question: 'What are you looking to do?',
    options: {
      buyHome: 'Buy a home',
      sellHome: 'Sell a home',
      moveToVancouver: 'Move to Vancouver',
      investmentOpportunities: 'Find investment opportunities',
      presale: 'Explore presales',
      justResearching: 'Just researching'
    }
  },
  presaleUse: {
    question: 'What is the presale mainly for?',
    options: {
      endUser: 'To live in',
      investor: 'As an investment'
    }
  },
  depositReadiness: {
    question: 'How ready is your presale deposit?',
    options: {
      fivePercentReady: '5% deposit ready',
      tenPercentReady: '10%+ deposit ready',
      buildingFunds: 'Building deposit funds',
      justLearning: 'Just learning how presales work'
    }
  },
  timeline: {
    question: {
      default: 'When are you planning to make a move?',
      seller: 'When are you planning to make a move?'
    },
    options: {
      asap: 'ASAP (0-3 months)',
      threeToSix: '3-6 months',
      sixToTwelve: '6-12 months',
      justExploring: 'Just exploring'
    }
  },
  propertyType: {
    question: {
      default: 'What type of property are you considering?',
      seller: 'What type of property are you planning to sell?'
    },
    options: {
      condo: 'Condo',
      townhouse: 'Townhouse',
      detached: 'Detached',
      investmentProperty: 'Investment property',
      notSureYet: 'Not sure yet'
    }
  },
  budget: {
    question: {
      default: 'What price range are you considering?',
      seller: 'What price range are you targeting?'
    },
    options: {
      under600: 'Under $600k',
      sixToNine: '$600k - $900k',
      nineToOneTwo: '$900k - $1.2M',
      oneTwoToTwo: '$1.2M - $2M',
      twoPlus: '$2M+'
    }
  },
  location: {
    question: {
      default: 'Which areas interest you?',
      relocation: 'Which areas interest you?'
    },
    description: 'The train symbol marks SkyTrain access.',
    options: {
      vancouver: 'Vancouver',
      burnaby: 'Burnaby',
      northShore: 'North Shore',
      richmond: 'Richmond',
      surreyLangley: 'Surrey / Langley',
      notSureYet: 'Not sure yet'
    }
  },
  results: {
    title: 'Your shortlist',
    continue: 'Send my shortlist',
    sections: {
      bestMatches: {
        title: '{{propertyType}} picks under {{budget}}',
        description: 'Listings that fit your budget and goals.'
      },
      presaleMatches: {
        title: 'Presales for {{budget}} buyers',
        description: 'Projects that match your timing, budget and deposit.'
      },
      presaleStrategy: {
        title: 'Your presale plan',
        description: 'Based on the deposit, completion timing and your plan.'
      },
      investorFocus: {
        title: 'Areas for investors near {{location}}',
        description: 'Strong rental demand and more room to negotiate.'
      },
      undervalued: {
        title: 'Undervalued areas near {{location}}',
        description: 'Pricing still below local fundamentals.'
      },
      priceDrops: {
        title: 'Recent price drops',
        description: 'Homes that cut their price this week.'
      },
      presales: {
        title: 'Upcoming presales',
        description: 'New projects that fit your timeline.'
      }
    }
  },
  capture: {
    title: 'Get your shortlist',
    subtitle: 'Get homes that match, and why each one fits.',
    nameLabel: 'Name',
    namePlaceholder: 'Your full name',
    emailLabel: 'Email',
    emailPlaceholder: 'name@email.com',
    phoneLabel: 'Phone',
    phonePlaceholder: '(604) 555-0123',
    phoneHelper: '',
    submit: 'Get the shortlist'
  },
  crossSell: {
    title: 'Not sure where to start?',
    description: 'Pick what you want to do and answer a few questions.',
    buttonLabel: 'Start here'
  },
  fallback: {
    incomplete: 'Some answers are missing. Restart to build your watchlist.',
    restart: 'Restart Property Match'
  },
  resumePrompt: {
    message: 'You have a session in progress.',
    resume: 'Resume from step {{step}}',
    startFresh: 'Start fresh'
  },
  actions: {
    back: 'Back',
    continue: 'Continue'
  },
  progress: {
    label: 'Step {{current}} of {{total}}'
  }
} as const;
